import { FC, ReactNode, useState } from 'react';

import { Button } from '@/components/atoms';
import { cn } from '@/utils/tailwind';

import {
  Dropdown,
  DropdownMenuClearSelection,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../Dropdown/Dropdown';

export type SortDropdownOption = {
  label: string;
  value: string;
  [key: string]: any;
};

type CategoryDropdownProps = {
  triggerLabel: string;
  triggerIcon?: ReactNode;
  triggerVariant?: 'default' | 'transparent';
  options: SortDropdownOption[];
  selectedOption: string | null;
  onChange?: (newSelectedOption: string | null) => void;
  onClickClearSeletion?: () => void;
};

export const SortDropdown: FC<CategoryDropdownProps> = ({
  triggerLabel,
  triggerIcon,
  triggerVariant,
  options,
  selectedOption,
  onChange = () => {},
  onClickClearSeletion,
}) => {
  const [isOpenDropdown, setIsOpenDropdown] = useState(false);

  const handleChangeOption = (option: SortDropdownOption) => {
    const isExist = selectedOption === option.value;

    onChange(isExist ? null : option.value);
  };

  const hasSelection = Boolean(selectedOption);

  const selectedOptionLabel = options.find(({ value }) => value === selectedOption)?.label;

  return (
    <Dropdown onOpenChange={setIsOpenDropdown}>
      <DropdownMenuTrigger variant={triggerVariant} asChild data-testid="sort-dropdown-trigger">
        <Button className="gap-2" variant="secondary">
          {triggerIcon}{' '}
          <span
            className={cn(
              'transition-colors mr-1',
              isOpenDropdown ? 'text-custom-warm-grey-700' : 'text-custom-warm-grey-500'
            )}
          >
            {hasSelection ? selectedOptionLabel : triggerLabel}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="min-w-[260px]" align="end">
        {options.map(option => (
          <DropdownMenuItem
            key={option.value}
            checked={Boolean(selectedOption === option.value)}
            onCheckedChange={() => handleChangeOption(option)}
          >
            {option.label}
          </DropdownMenuItem>
        ))}
        {hasSelection && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuClearSelection onClick={onClickClearSeletion}>
              Clear selection
            </DropdownMenuClearSelection>
          </>
        )}
      </DropdownMenuContent>
    </Dropdown>
  );
};
